import { z } from 'zod';
import { platformEffectiveModuleSchema } from '../capabilities/platform-capability.contract.js';
import { platformJsonValueSchema } from '../settings/platform-setting-definition.contract.js';
import { platformApprovedBrandAssetsSchema } from '../brand-assets/platform-brand-asset.contract.js';
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const semanticVersionPattern = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[A-Za-z-][0-9A-Za-z-]*)(?:\.(?:0|[1-9]\d*|\d*[A-Za-z-][0-9A-Za-z-]*))*))?$/;
export const platformSemanticVersionSchema = z.string().regex(semanticVersionPattern);
function parsePlatformSemanticVersion(version) {
    const match = semanticVersionPattern.exec(version);
    if (match === null || match[1] === undefined || match[2] === undefined || match[3] === undefined) {
        return null;
    }
    return {
        core: [BigInt(match[1]), BigInt(match[2]), BigInt(match[3])],
        prerelease: match[4] === undefined ? null : match[4].split('.'),
    };
}
function comparePrerelease(left, right) {
    const length = Math.max(left.length, right.length);
    for (let index = 0; index < length; index += 1) {
        const leftItem = left[index];
        const rightItem = right[index];
        if (leftItem === undefined)
            return -1;
        if (rightItem === undefined)
            return 1;
        if (leftItem === rightItem)
            continue;
        const leftNumeric = /^\d+$/.test(leftItem);
        const rightNumeric = /^\d+$/.test(rightItem);
        if (leftNumeric && rightNumeric) {
            const leftNumber = BigInt(leftItem);
            const rightNumber = BigInt(rightItem);
            return leftNumber === rightNumber ? 0 : leftNumber < rightNumber ? -1 : 1;
        }
        if (leftNumeric)
            return -1;
        if (rightNumeric)
            return 1;
        return leftItem < rightItem ? -1 : 1;
    }
    return 0;
}
export function platformClientVersionAtLeast(actual, required) {
    const actualVersion = parsePlatformSemanticVersion(actual);
    const requiredVersion = parsePlatformSemanticVersion(required);
    if (actualVersion === null || requiredVersion === null)
        return false;
    for (let index = 0; index < 3; index += 1) {
        if (actualVersion.core[index] > requiredVersion.core[index])
            return true;
        if (actualVersion.core[index] < requiredVersion.core[index])
            return false;
    }
    if (actualVersion.prerelease === null)
        return true;
    if (requiredVersion.prerelease === null)
        return false;
    return comparePrerelease(actualVersion.prerelease, requiredVersion.prerelease) >= 0;
}
export const platformEffectiveConfigurationSchema = z.strictObject({
    schemaVersion: z.literal(1),
    releaseId: z.string().uuid().nullable(),
    revision: z.number().int().nonnegative(),
    registryVersion: z.string().min(1),
    settingSchemaVersions: z.record(z.string(), z.number().int().positive()),
    checksum: sha256Schema,
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    values: z.record(z.string(), platformJsonValueSchema),
});
export const platformRuntimeBootstrapSchema = z.strictObject({
    data: z.strictObject({
        schemaVersion: z.literal(1),
        tenant: z.strictObject({
            id: z.string().uuid(),
            slug: z.string(),
            configurationReleaseId: z.string().uuid().nullable(),
        }),
        actor: z.strictObject({
            userId: z.string().uuid(),
            type: z.enum(['user', 'public-link', 'webhook', 'worker', 'system']),
        }),
        membership: z.strictObject({
            id: z.string().uuid(),
            roleKeys: z.array(z.string()),
        }),
        revisions: z.strictObject({
            membership: z.number().int().positive(),
            permissions: z.number().int().positive(),
            session: z.number().int().positive(),
            entitlements: z.number().int().nonnegative(),
            configuration: z.number().int().nonnegative(),
            access: sha256Schema,
        }),
        deployment: z.strictObject({
            activationEpoch: z.number().int().nonnegative(),
            releaseCatalogHash: sha256Schema,
            minimumClientVersions: z.strictObject({
                web: platformSemanticVersionSchema,
                mobile: platformSemanticVersionSchema.nullable(),
            }),
        }),
        locale: z.string().min(2).max(35),
        modules: z.array(platformEffectiveModuleSchema),
        permissions: z.array(z.string()),
        configuration: platformEffectiveConfigurationSchema,
        approvedBrandAssets: platformApprovedBrandAssetsSchema,
    }),
});
//# sourceMappingURL=platform-runtime-bootstrap.contract.js.map